import { createInjectable } from 'ngxtension/create-injectable';
import { computed, inject, linkedSignal, signal } from '@angular/core';
import { injectPocketbase } from '@app/core/tokens/pocketbase.token';
import { createLogger } from '@app/shared/util/logger';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from "@capacitor/core";
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";

type User = {
  avatar: string,
  collectionId: string,
  collectionName: string,
  created: string;
  email: string;
  emailVisibility: boolean;
  id: string;
  name: string;
  updated: string;
  verified: boolean;
  zip?: string;
  city?: string;
  age?: string;
  dialectProficiency?: number;
  highestEducation?: string;
  gender?: string;
  languageAtWork?: string;
  languageWithBestFriend?: string;
  dialectInTextMessages?: string;
  livedWithoutDialect?: boolean;
  proudOfDialect?: number;
  findDialectBeautiful?: number;
  dialectDifferenceByAge?: number;
  notifications?: {
    questionnaires?: boolean;
    news?: boolean;
  }
} | null;

export const AuthService = createInjectable(() => {
  const { log, warn } = createLogger('auth.service');
  const pb = injectPocketbase();
  const http = inject(HttpClient);
  const router = inject(Router);
  const onAfterLoginCallbacks: Set<() => void> = new Set<() => void>();

  if (pb.authStore.isValid) {
    pb.collection('users').authRefresh()
      .then(() => {
        log('refreshed successfully');

        if (pb.authStore.record?.id) {
          checkExternalAuth();
        }
      })
      .catch((e) => {
        warn('failed to refresh', e);

        pb.authStore.clear();
        router.navigate(['/login'], {
          replaceUrl: true,
        })
      });
  } else {
    pb.authStore.clear();
  }

  const idToken = signal(pb.authStore.token);
  const user = signal<User>(pb.authStore.record as User);
  const isLoggedIn = computed(() => Boolean(user()));
  const externalAuthProviders = signal<string[]>([]);
  const isExternalAuth = computed(() => externalAuthProviders().length > 0);
  const requireOnboarding = computed(() => isLoggedIn() && (!user()!.age || !user()!.zip || !user()!.city || (!user()!.name && !user()!.email.endsWith('.invalid'))));
  const isGuest = computed(() => Boolean(user()?.email.endsWith('.invalid')));
  const isFirstUsage = signal(false);
  const showEmailConfirmationHint = linkedSignal(() => !isGuest() && !isFirstUsage() && !user()?.verified);

  log('auth store initialized', user());

  pb.authStore.onChange(((token, authModel) => {
    if (token !== idToken()) {
      log('auth store token change');

      idToken.set(token);
    }

    if (JSON.stringify(authModel) !== JSON.stringify(user())) {
      const isLogin = user() === null;
      log('auth store model change', authModel);

      user.set(authModel as User);

      if (isLogin) {
        log('on after login');
        checkExternalAuth();

        if (!requireOnboarding() && onAfterLoginCallbacks.size > 0) {
          log('trigger onAfterLoginCallbacks', onAfterLoginCallbacks.size);

          onAfterLoginCallbacks.forEach((cb) => {
            cb();

            onAfterLoginCallbacks.delete(cb);
          });
        }
      }
    }

    if (authModel?.id) {
      Sentry.setUser({
        id: authModel.id
      });
    }
  }));

  let resumeListener: PluginListenerHandle;
  App.addListener('resume', () => {
    if (showEmailConfirmationHint()) {
      pb.collection('users').authRefresh<User>().then(({ record }) => {
        if (record?.verified) {
          showEmailConfirmationHint.set(false);
          resumeListener!.remove();
        }
      }).catch((e) => {
        warn('failed to refresh', e);

        pb.authStore.clear();
        router.navigate(['/login'], {
          replaceUrl: true,
        })
      })
    }
  }).then((listener) => resumeListener = listener);

  function generateAlphanumericUID(length = 15) {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let uid = '';
    for (let i = 0; i < length; i++) {
      uid += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return uid;
  }

  function logout(redirect = false) {
    pb.authStore.clear();
    if (redirect) {
      router.navigate(['/login'], {
        replaceUrl: true,
      });
    }
  }

  function checkExternalAuth() {
    if (!pb.authStore.record?.id) {
      return;
    }

    pb.collection('_externalAuths').getFullList({
      filter: `recordRef = "${pb.authStore.record.id}"`
    })
      .then((response) => {
        if (response.length > 0) {
          const providers = response
            .map((authData) => (authData as any).provider)
            .filter(Boolean);

          log('external auth data found', providers);
          externalAuthProviders.set(providers);
        }
      })
      .catch(_ => {
        // noop
      });
  }

  return {
    isLoggedIn,
    requireOnboarding,
    user: user.asReadonly(),
    idToken: idToken.asReadonly(),
    isGuest,
    isExternalAuth,
    isVerified: computed(() => user()?.verified ?? false),
    showEmailConfirmationHint,
    avatar: computed(() => {
      if (user()?.avatar) {
        return pb.files.getURL(user()!, user()!.avatar);
      }

      return null;
    }),
    addAfterLoginCallback: (cb: () => void) => onAfterLoginCallbacks.add(cb),
    changeEmail: (email: string) => pb.collection('users').requestEmailChange(email),
    forgotPassword: (email: string) => pb.collection('users').requestPasswordReset(email),
    loginWithPassword: (email: string, password: string) => pb.collection('users').authWithPassword(email, password),
    loginWithGoogle: () => pb.collection('users').authWithOAuth2({ provider: 'google' }),
    loginWithApple: () => pb.collection('users').authWithOAuth2({ provider: 'apple' }),
    loginWithFacebook: () => pb.collection('users').authWithOAuth2({ provider: 'facebook' }),
    logout,

    isUsernameAvailable: (username: string) => pb.collection('users')
      .getList(1, 1, {
        filter: `name="${username}"`
      })
      .then((result) => result.items.length === 0),

    updateUserProfile: (profile: Partial<User>) => pb.collection('users').update(
      user()?.id as string,
      profile as Record<string, any>
    ),

    register: async (email: string, password: string) => {
      await pb.collection('users').create({
        email,
        password,
        passwordConfirm: password,
      });

      await pb.collection('users').requestVerification(email);

      isFirstUsage.set(true);
    },

    createGuestUser: async () => {
      const randomId = generateAlphanumericUID();
      const email = `guest_${randomId}@guest.invalid`;

      await pb.collection('users').create({
        id: randomId,
        email,
        password: randomId,
        passwordConfirm: randomId,
      });

      return pb.collection('users').authWithPassword(email, randomId);
    },

    upgradeGuestUser: async (email: string, password: string, username: string) => {
      try {
        await lastValueFrom(http.post(
          `${environment.backendUrl}/api/v1/user/upgrade`,
          {
            email,
            password,
            username
          }
        ));
        await pb.collection('users').authWithPassword(email, password);
        await pb.collection('users').requestVerification(email);
      } catch (e: any) {
        warn(e);
      }
    },

    deleteAccount: async () => {
      if (user()) {
        try {
          await lastValueFrom(
            http.delete(`${environment.backendUrl}/api/v1/user`)
          );

          logout(true);
        } catch (e: any) {
          warn (e);
        }
      }
    }
  };
});
